import { computed } from "vue";
import { queryOptions, useQuery, type QueryClient } from "@tanstack/vue-query";
import type { PortalSettings } from "@ai-platform/contracts";
import { callRoute } from "../api/client.ts";
import { routeKey } from "../api/query-keys.ts";

/*
 * What the portal needs to know before anyone signs in (`GET /api/settings`, ADR-133 point 3):
 * whether guests see the catalogue (`GUEST_CATALOG_ENABLED` at the API). The router's guard reads
 * it next to the session; the layout, the inquiry dialog and the login follow it.
 */

/** Key of the settings in the query cache, by the route catalogue (ADR-076). */
export const SETTINGS_KEY = routeKey("settings");

/**
 * What holds while the API cannot tell: the guest view on, as the API has it without the
 * variable. A guest then meets the API's own refusal at worst, never a lock the API has not set.
 */
export const DEFAULT_SETTINGS: PortalSettings = { guestCatalog: true };

/**
 * `GET /api/settings`. A failure goes on to the query, which caches nothing for it (GP4): the
 * defaults stand in for the moment (`settingsFor`, `usePortalSettings`), and the next navigation
 * asks again. The query client reports an answer outside the contract (PF4).
 */
export function fetchSettings(): Promise<PortalSettings> {
  return callRoute("settings");
}

/**
 * The settings query: read once per load of the page. The API answers `no-store`, so a changed
 * setting holds at the next load, as ADR-133 point 3 has it. Only an answer stays; after a
 * failure the query holds no data and is read again by the next guard. No retry: the guard waits
 * for this read, and the defaults serve until the next navigation asks again.
 */
export function settingsQueryOptions() {
  return queryOptions({ queryKey: SETTINGS_KEY, queryFn: fetchSettings, staleTime: Infinity, retry: false });
}

/**
 * The settings for the guard: from the cache once read, else from the API; while the API cannot
 * tell, the defaults, so the portal still opens, without keeping them (GP4).
 */
export async function settingsFor(queryClient: QueryClient): Promise<PortalSettings> {
  try {
    return await queryClient.query(settingsQueryOptions());
  } catch {
    return DEFAULT_SETTINGS;
  }
}

/**
 * What components see while the answer is on its way, at the first load before the guard has read
 * it: no guest view. Better a moment without the catalogue than a read the API refuses with 401.
 */
const PENDING_SETTINGS: PortalSettings = { guestCatalog: false };

/**
 * The settings for components; no guest view until the answer is there; after a failure the
 * defaults, as the guard has them (`settingsFor`), until an answer comes.
 */
export function usePortalSettings() {
  const query = useQuery(settingsQueryOptions());
  return computed<PortalSettings>(
    () => query.data.value ?? (query.isError.value ? DEFAULT_SETTINGS : PENDING_SETTINGS),
  );
}
